import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { motion } from 'framer-motion';
import '../css/About.css';

const values = [
    {
        n: '01',
        title: 'Fit-for-purpose parts',
        body: 'Spares aligned to your application so you avoid trial-and-error downtime.',
    },
    {
        n: '02',
        title: 'Clarity on price & lead time',
        body: 'Straight answers so maintenance and procurement can plan together.',
    },
    {
        n: '03',
        title: 'Technical backup',
        body: 'Help with specs and alternates when the usual supply chain slows down.',
    },
];

const About = () => {
    return (
        <div className="about-page about-page--v2">
            <Header />
            <main className="about-main">
                <section className="about-intro">
                    <div className="container">
                        <div className="row align-items-lg-stretch g-4 g-lg-4 g-xl-5">
                            <motion.div
                                className="col-lg-3 col-xl-2"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <p className="about-kicker">About the company</p>
                                <div
                                    className="about-year-block"
                                    aria-hidden="true"
                                >
                                    <span className="about-year">2015</span>
                                    <span className="about-year-caption">
                                        incorporated
                                    </span>
                                </div>
                            </motion.div>
                            <motion.div
                                className="col-lg-6 col-xl-7"
                                initial={{ opacity: 0, y: 28 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: 0.06 }}
                            >
                                <h1 className="about-headline">
                                    Spares and support for production that{' '}
                                    <span className="about-headline-mark">
                                        keeps moving
                                    </span>
                                    .
                                </h1>
                                <p className="about-dek">
                                    <strong>
                                        INDUSTRIAL PARTS AND ENGINEERING SERVICES LTD
                                    </strong>{' '}
                                    supports Ugandan manufacturing with components and
                                    guidance—from drives and starters to protection and
                                    control—so your team spends less time chasing parts
                                    and more time making product.
                                </p>
                            </motion.div>
                            <motion.aside
                                className="col-lg-3 col-xl-3"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.12 }}
                                aria-label="Company snapshot"
                            >
                                <div className="about-intro-aside">
                                    <p className="about-aside-title">At a glance</p>
                                    <ul className="about-aside-list">
                                        <li>
                                            <span className="about-aside-label">
                                                Registered
                                            </span>
                                            <span className="about-aside-value">
                                                May 2015
                                            </span>
                                        </li>
                                        <li>
                                            <span className="about-aside-label">
                                                Market
                                            </span>
                                            <span className="about-aside-value">
                                                B2B industrial, Uganda
                                            </span>
                                        </li>
                                        <li>
                                            <span className="about-aside-label">
                                                Focus
                                            </span>
                                            <span className="about-aside-value">
                                                Spares, sourcing &amp; technical
                                                support
                                            </span>
                                        </li>
                                    </ul>
                                    <Link
                                        to="/contact-us"
                                        className="about-aside-link"
                                    >
                                        Get in touch →
                                    </Link>
                                </div>
                            </motion.aside>
                        </div>
                    </div>
                </section>

                <div className="about-band" role="presentation">
                    <div className="about-marquee">
                        <span>Uganda</span>
                        <span className="about-marquee-dot" aria-hidden>
                            ·
                        </span>
                        <span>B2B industrial</span>
                        <span className="about-marquee-dot" aria-hidden>
                            ·
                        </span>
                        <span>Since May 2015</span>
                        <span className="about-marquee-dot" aria-hidden>
                            ·
                        </span>
                        <span>Quality spares</span>
                        <span className="about-marquee-dot about-marquee-dot--hide-sm" aria-hidden>
                            ·
                        </span>
                        <span className="about-marquee-extra">Technical sourcing</span>
                    </div>
                </div>

                <section className="about-bento-wrap">
                    <div className="container">
                        <div className="about-bento">
                            <motion.article
                                className="about-bento-cell about-bento-cell--story"
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.45 }}
                            >
                                <h2 className="about-bento-label">Our story</h2>
                                <p className="about-bento-text">
                                    Incorporated in May 2015, we set out to give
                                    manufacturing industries quality spares at the
                                    right cost, in the right quantities, at the right
                                    time, and in the right place. That mission still
                                    drives how we quote, pack, and follow up.
                                </p>
                                <p className="about-bento-text mb-0">
                                    Today we work beside factories and maintenance crews
                                    to cut downtime, clarify specifications, and source
                                    dependable alternatives when original lines are
                                    delayed or discontinued.
                                </p>
                            </motion.article>
                            <motion.aside
                                className="about-bento-cell about-bento-cell--pull"
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.45, delay: 0.08 }}
                            >
                                <blockquote className="about-pullquote">
                                    <p>
                                        We don&apos;t only ship parts—we help keep your
                                        lines running with advice you can act on.
                                    </p>
                                </blockquote>
                            </motion.aside>
                        </div>
                    </div>
                </section>

                <section className="about-values-wrap">
                    <div className="container">
                        <motion.h2
                            className="about-values-heading"
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4 }}
                        >
                            What you can expect
                        </motion.h2>
                        <ol className="about-values-list">
                            {values.map((item, index) => (
                                <motion.li
                                    key={item.n}
                                    className="about-value-row"
                                    initial={{ opacity: 0, x: -16 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: '-30px' }}
                                    transition={{
                                        duration: 0.4,
                                        delay: index * 0.07,
                                    }}
                                >
                                    <span className="about-value-num">{item.n}</span>
                                    <div className="about-value-body">
                                        <h3 className="about-value-title">
                                            {item.title}
                                        </h3>
                                        <p className="about-value-text mb-0">
                                            {item.body}
                                        </p>
                                    </div>
                                </motion.li>
                            ))}
                        </ol>
                    </div>
                </section>

                <section className="about-outro">
                    <div className="container">
                        <motion.div
                            className="about-outro-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45 }}
                        >
                            <div className="about-outro-text">
                                <h2 className="about-outro-title">Next step</h2>
                                <p className="about-outro-desc mb-0">
                                    Request a quote, ask a technical question, or meet
                                    the people behind Industrial Parts (U) Ltd.
                                </p>
                            </div>
                            <div className="about-outro-actions">
                                <Link
                                    to="/contact-us"
                                    className="about-link-btn about-link-btn--solid"
                                >
                                    Contact
                                </Link>
                                <Link
                                    to="/team"
                                    className="about-link-btn about-link-btn--line"
                                >
                                    Team
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default About;
